import { Injectable } from '@angular/core';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import type { BackendErrorResponse } from '@freelancer/freelancer-http';
import {
  FreelancerHttp,
  FreelancerHttpService,
  HttpEventType,
} from '@freelancer/freelancer-http';
import { isDefined, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as ExifReader from 'exifreader';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import {
  filter,
  last,
  map,
  shareReplay,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import type { HttpResponse as TusResponse } from 'tus-js-client';
import { Upload as TusUpload } from 'tus-js-client';
import { v4 as uuidv4 } from 'uuid';
import type {
  BackendGetDownloadURLReturnTypes,
  BackendUploadReturnTypes,
  NewFileServiceGetDownloadUrlReturnType,
} from './file-storage-backend.config';
import { configs } from './file-storage-backend.config';
import type {
  FileId,
  FullMetadata,
  SettableMetadata,
  StorageBucket,
  UploadTaskEvent,
} from './file-storage.model';

export class UploadTask {
  private upload$: Observable<UploadTaskEvent>;
  private unsubscribeSubject$ = new Subject<void>();
  private unsubscribe$ = this.unsubscribeSubject$.asObservable();

  constructor(rawUpload$: Observable<UploadTaskEvent>) {
    // allows to unsubscribe all listeners on cancel
    this.upload$ = rawUpload$.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),

      takeUntil(this.unsubscribe$),
    );
    // starts the upload. subscription is ignored because of the takeUntil above.
    // eslint-disable-next-line local-rules/no-ignored-subscription
    this.upload$.subscribe();
  }

  /*
   * Emits the UploadTaskEvent as the file upload progresses.
   * Completes when the upload has completed (success or error),
   * i.e. you can use `last` to only get the upload result:
   * this.snapshot = this.task.changes().pipe(
   *   last(() =>  {
   *     this.downloadURL = fileRef.getDownloadURL() )
   */
  changes(): Observable<UploadTaskEvent> {
    return this.upload$;
  }

  /**
   * Emits the UploadTaskEvent when it completes (success or error)
   * i.e. you can use this to only get the upload result.
   */
  completes(): Observable<UploadTaskEvent> {
    return this.upload$.pipe(last());
  }

  /*
   * Emits the upload progress as a number between 0 and 100, that can easily
   * be piped into a <progress> element's value or similar Bits component
   */
  percentageChanges(): Observable<number> {
    return this.upload$.pipe(
      map(s => Math.min((s.bytesTransferred / s.totalBytes) * 100, 100)),
    );
  }

  /*
   * Emits the download URL of the file once the upload is completed.
   * Use that to pipe it to image source for instance
   * (<img [src]="profileUrl | flAsync" />), but not for user-triggered
   * actions as there's no error handling (it'll just never emit).
   */
  getDownloadURL(): Observable<string> {
    return this.upload$.pipe(
      last(),
      map(s => (s.state !== 'error' ? s.downloadURL : undefined)),
      filter(isDefined),
    );
  }

  /*
   * Cancel an upload in progress.
   */
  cancel(): void {
    this.unsubscribeSubject$.next();
    this.unsubscribeSubject$.complete();
  }
}

export interface DownloadURLSuccessResponse {
  status: 'success';
  downloadURL: string;
}

export interface GetMetadataSuccessResponse {
  status: 'success';
  metadata: FullMetadata;
}

export interface ImageDimensionParams {
  readonly width: number;
  readonly height: number;
}

export interface CachedDownloadUrl {
  readonly downloadURL: string;
  readonly expires: number;
}

@UntilDestroy({ className: 'FileStorage' })
@Injectable({
  providedIn: 'root',
})
export class FileStorage {
  constructor(private freelancerHttp: FreelancerHttp) {}
  /**
   * This will automatically rotate an image based on the orientation details.
   *
   * It is required as some images (especially those taken on mobile devices)
   * would store an image in a not rotated format, but will leave it up to
   * image viewers to rotate the image based on orientation data.
   * Without this logic, images may be saved and appear with the incorrect
   * orientation.
   *
   * This works by showing the image in a canvas, and lets the canvas
   * automatically do the rotation based on the EXIF details, and then saving
   * that as a new image with the processing already done.
   */
  private rotateImageToOrientation(file: File): Promise<File> {
    // Read the file as a Data URL so it can be loaded into an HTML Canvas.
    const fileReader = new FileReader();

    return new Promise(resolve => {
      fileReader.onload = ev => {
        // Get the data URL.
        const originalDataUrl = ev.target?.result;
        let newFile: File;

        // Create a HTML Canvas.
        const canvas = document.createElement('canvas');
        const img = new Image();

        // Convert the data URL to a blob so eventually we can use it as a
        // file.
        const dataURLtoBlob = (dataurl: string): Blob => {
          const arr = dataurl.split(',');
          const mime = arr[0].match(/:(.*?);/);
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new Blob([u8arr], {
            type: mime && mime[1] ? mime[1] : 'image/jpeg',
          });
        };

        const drawImage = (): void => {
          // Create canvas context.
          const ctx = canvas.getContext('2d');

          // Assign width and height.
          canvas.width = img.width;
          canvas.height = img.height;

          ctx?.translate(canvas.width / 2, canvas.height / 2);
          ctx?.drawImage(img, -img.width / 2, -img.height / 2);

          // Turn the blob into a file so it can be used in the existing way
          // for upload.
          newFile = new File(
            [dataURLtoBlob(canvas.toDataURL(file.type))],
            `photo.${file.type.split('/')[1]}`,
          );

          resolve(newFile);
        };

        img.onload = () => {
          drawImage();
        };

        // Load the image.
        img.src = originalDataUrl as string;
      };

      // Trigger the file read.
      fileReader.readAsDataURL(file);
    });
  }

  /*
   * Upload a new photo to the storage bucket.
   *
   * This function is specifically for uploading photos that may have rotation
   * data attached to it that would be displayed on our website, specifically
   * profile photos, cover photos, etc.
   *
   * It is required as some devices will store photos at the wrong angle, but
   * would store orientation information in EXIF. This would leave it up to the
   * photo viewer to decide how to handle displaying the photo, rather than
   * storing it with the correct rotation details.
   *
   * This will handle any case where the photos are stored with the wrong
   * orientation, correct that, then upload the photo.
   */
  async uploadPhoto(
    bucket:
      | StorageBucket.PROFILE_COVER_IMAGES
      | StorageBucket.PROFILE_PICTURES
      | StorageBucket.GROUP_COVER_IMAGE,
    file: File,
    metadata?: SettableMetadata,
  ): Promise<UploadTask> {
    // In the case it's a JPG/JPEG, then let's check if it needs to be stored
    // rotated based on its EXIF orientation.
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      const rotatedFile = await ExifReader.load(file).then(async exif => {
        if (exif.Orientation) {
          return this.rotateImageToOrientation(file);
        }
        return file;
      });
      return this.upload(bucket, rotatedFile, metadata);
    }
    return this.upload(bucket, file, metadata);
  }

  upload(
    bucket: StorageBucket,
    file: File,
    metadata?: SettableMetadata,
  ): UploadTask {
    if (!bucket) {
      throw new Error('Required parameter {bucket} is not defined!');
    }
    const id = uuidv4();
    const config = configs[bucket]({ bucket, id });
    if (config.isLegacy) {
      return this.uploadLegacy(bucket, id, file, metadata);
    }
    return this.uploadNew(bucket, id, file, metadata);
  }

  /*
   * Replace an existing file, specified by its file id, with a new one.
   */
  replace(
    bucket: StorageBucket,
    id: FileId,
    file: File,
    metadata?: SettableMetadata,
  ): UploadTask {
    const config = configs[bucket]({ bucket, id });
    // Not implemented for non-legacy endpoints yet
    if (!config.isLegacy) {
      throw new Error('Not implemented');
    }
    return this.uploadLegacy(bucket, id, file, metadata);
  }

  /*
   * Not implemented, but feel free to
   */
  delete(bucket: StorageBucket, id: FileId): Promise<void> {
    throw new Error('Not implemented');
  }

  getDownloadURL(
    bucket: StorageBucket,
    id: FileId,
    customMetadata: { [k: string]: string } = {},
  ): Promise<
    DownloadURLSuccessResponse | BackendErrorResponse<BackendAllErrorCodes>
  > {
    const config = configs[bucket]({ bucket, id });
    if (config.isLegacy) {
      return this.getDownloadUrlLegacy(bucket, id, customMetadata);
    }
    return this.getDownloadUrlNew(bucket, id, customMetadata);
  }

  private getDownloadUrlFromCache(cacheKey: string): string | undefined {
    const rawDataFromCache = window.sessionStorage.getItem(cacheKey);
    if (!rawDataFromCache) {
      return undefined;
    }

    const dataFromCache = JSON.parse(rawDataFromCache) as CachedDownloadUrl;
    if (dataFromCache.expires > Date.now()) {
      return dataFromCache.downloadURL;
    }

    window.sessionStorage.removeItem(cacheKey);
    return undefined;
  }

  private getDownloadUrlNew(
    bucket: StorageBucket,
    id: FileId,
    customMetadata: { [k: string]: string } = {},
  ): Promise<
    DownloadURLSuccessResponse | BackendErrorResponse<BackendAllErrorCodes>
  > {
    const config = configs[bucket]({ bucket, id });
    if (config.isLegacy) {
      throw new Error('This storage bucket does not support legacy downloads');
    }

    const cacheKey = `fileStorage-downloadUrl-new-${bucket}-${id}-${JSON.stringify(
      customMetadata,
    )}`;
    if (config.urlCacheable) {
      const downloadUrlFromCache = this.getDownloadUrlFromCache(cacheKey);
      if (downloadUrlFromCache) {
        return Promise.resolve({
          status: 'success' as const,
          downloadURL: downloadUrlFromCache,
        });
      }
    }

    // TODO: T267853 - Make this fallback to a generic download URL endpoint that is baked
    // into a future file drive service that supports ACL checks
    const downloadUrlEndpoint =
      config.customDownloadUrlEndpoint?.(customMetadata);

    if (!downloadUrlEndpoint) {
      throw new Error(
        'Storage backend does not support getting the file download URL',
      );
    }

    return firstValueFrom(
      this.freelancerHttp
        .get<NewFileServiceGetDownloadUrlReturnType>(downloadUrlEndpoint)
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status === 'success') {
        if (config.urlCacheable) {
          // Try to parse and save the download URL in cache
          // Try/catch and ignore errors since this is non-critical
          try {
            const urlExpiry = new URLSearchParams(
              new URL(response.result.url).search,
            ).get('expires');
            if (urlExpiry) {
              window.sessionStorage.setItem(
                cacheKey,
                JSON.stringify({
                  // convert second timestamp into milliseconds
                  expires: toNumber(urlExpiry) * 1000,
                  downloadURL: response.result.url,
                } as CachedDownloadUrl),
              );
            }
          } catch (e) {
            // Failed to cache - do nothing
          }
        }
        return {
          status: 'success' as const,
          downloadURL: response.result.url,
        };
      }
      return {
        status: 'error' as const,
        errorCode: response.errorCode,
        requestId: response.requestId,
      };
    });
  }

  /*
   * Returns an standard error object when the download fails, e.g. due to a
   * permission error. The FileStorage service has not and MUST not have any
   * concept of permisions, i.e. the DriveFile model is just plain wrong.
   */
  private getDownloadUrlLegacy(
    bucket: StorageBucket,
    id: FileId,
    customMetadata: { [k: string]: string } = {},
  ): Promise<
    DownloadURLSuccessResponse | BackendErrorResponse<BackendAllErrorCodes>
  > {
    const config = configs[bucket]({ bucket, id });
    if (!config.isLegacy) {
      throw new Error('Only legacy storage buckets can use this function');
    }

    const downloadUrlEndpoint =
      config.downloadURLEndpoint ??
      config.DEPRECATED_funkyChangingDownloadUrlEndpoint?.(customMetadata);

    if (!downloadUrlEndpoint) {
      throw new Error(
        'Storage backend does not support getting the file download URL',
      );
    }

    return firstValueFrom(
      this.freelancerHttp
        .get<BackendGetDownloadURLReturnTypes[typeof bucket]>(
          downloadUrlEndpoint,
        )
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status === 'success') {
        if (!config.downloadURL) {
          throw new Error(
            'No downloadURL parsing method has been defined for that storage',
          );
        }
        return {
          status: 'success' as const,
          downloadURL: config.downloadURL(response.result as any),
        };
      }
      return {
        status: 'error' as const,
        errorCode: response.errorCode,
        requestId: response.requestId,
      };
    });
  }

  /*
   * Returns the image dimensions of an image file. Taken from:
   * https://stackoverflow.com/a/7460303.
   */
  getImageDimensionsFromFile(file: File): Promise<ImageDimensionParams> {
    return new Promise(resolve => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        const image = new Image();
        image.src = fileReader.result as string;

        image.onload = () => {
          resolve({
            width: image.width,
            height: image.height,
          });
        };
      };
    });
  }

  /*
   * Not implemented, but feel free to
   */
  getMetadata(
    bucket: StorageBucket,
    id: FileId,
  ): Promise<
    GetMetadataSuccessResponse | BackendErrorResponse<BackendAllErrorCodes>
  > {
    throw new Error('Not implemented');
  }

  /*
   * Not implemented, but feel free to
   */
  updateMetadata(
    bucket: StorageBucket,
    id: FileId,
    metadata: SettableMetadata,
  ): Promise<
    GetMetadataSuccessResponse | BackendErrorResponse<BackendAllErrorCodes>
  > {
    throw new Error('Not implemented');
  }

  /**
   * Uploads a file using the new file service.
   */
  private uploadNew(
    bucket: StorageBucket,
    id: FileId,
    file: File,
    metadata?: SettableMetadata,
  ): UploadTask {
    const uploadMetadata = {
      ...(metadata || {}),
      bucket,
      id,
      name: file.name,
      size: file.size,
      timeCreated: Date.now(),
      updated: Date.now(),
    };
    const config = configs[bucket]({ bucket, id });
    if (config.isLegacy) {
      throw new Error('This storage bucket only supports legacy uploads.');
    }

    const startEvent: UploadTaskEvent = {
      state: 'running' as const,
      ref: { bucket, id },
      metadata: uploadMetadata,
      bytesTransferred: 0,
      totalBytes: file.size,
    };

    const endpoint = this.freelancerHttp.getBaseServiceUrl(
      FreelancerHttpService.FILE_SERVICE,
    );

    const upload$ = new Observable<UploadTaskEvent>(observer => {
      const uploadSession = new TusUpload(file, {
        // Limt max chunk size to 250 MB
        chunkSize: 262_144_000,
        // Endpoint is the upload creation URL from your tus server
        endpoint,
        // Retry delays will enable tus-js-client to automatically retry on errors
        retryDelays: [0, 3000, 5000],
        // Attach additional meta data about the file for the server
        metadata: {
          fileId: id,
          fileName: file.name,
          fileType: file.type,
        },
        // Callback for errors which cannot be fixed using retries (i.e. any 4XX)
        onError(tusError: Error): void {
          // the tus-js-client library doesn't type the error it returns, so we
          // parse it properly to get the real error from our backend.
          const err = parseTusError(tusError);

          // populate our fields to emit & emit as an error
          observer.next({
            ...startEvent,
            state: 'error' as const,
            error: err,
          });
          observer.complete();
        },
        onProgress: (bytesUploaded: number) => {
          observer.next({
            ...startEvent,
            bytesTransferred: bytesUploaded,
          });
        },
        onSuccess: () => {
          // We emit the final event indicating success
          observer.next({
            ...startEvent,
            bytesTransferred: file.size,
            state: 'success' as const,
          });

          // This is the last event, so we complete the observable
          observer.complete();
        },
      });
      uploadSession.start();

      return () => {
        uploadSession.abort();
      };
    }).pipe(startWith(startEvent));
    return new UploadTask(upload$);
  }

  /**
   * Uploads a file using the legacy file service(s).
   */
  private uploadLegacy(
    bucket: StorageBucket,
    id: FileId,
    file: File,
    metadata?: SettableMetadata,
  ): UploadTask {
    const config = configs[bucket]({ bucket, id });
    if (!config.isLegacy) {
      throw new Error('This storage bucket does not support legacy uploads');
    }

    const uploadMetadata = {
      ...(metadata || {}),
      bucket,
      id,
      name: file.name,
      size: file.size,
      timeCreated: Date.now(),
      updated: Date.now(),
    };

    const formData = new FormData();
    formData.append(config.fileParam, file);
    formData.append('metadata', JSON.stringify(uploadMetadata));
    if (config.DEPRECATED_extraParams) {
      Object.entries(config.DEPRECATED_extraParams(uploadMetadata)).forEach(
        ([key, value]) => {
          if (value !== undefined) {
            formData.append(key, value);
          }
        },
      );
    }

    const startEvent: UploadTaskEvent = {
      state: 'running' as const,
      ref: { bucket, id },
      metadata: uploadMetadata,
      bytesTransferred: 0,
      totalBytes: file.size,
    };

    const upload$ = this.freelancerHttp
      .post<BackendUploadReturnTypes[typeof bucket]>(
        config.DEPRECATED_funkyChangingEndpoint
          ? config.DEPRECATED_funkyChangingEndpoint(uploadMetadata)
          : config.endpoint,
        formData,
        {
          reportProgress: true,
          isGaf: config.isGaf || false,
        },
      )
      .pipe(
        map(event => {
          if (event.type === HttpEventType.UploadProgress) {
            return {
              ...startEvent,
              bytesTransferred: event.loaded,
            } as UploadTaskEvent;
          }
          if (event.type === HttpEventType.Response && event.body) {
            if (event.body.status === 'success') {
              return {
                ...startEvent,
                state: 'success' as const,
                bytesTransferred: startEvent.totalBytes,
                downloadURL: config.downloadURL
                  ? config.downloadURL(event.body.result as any)
                  : undefined,
                ref: {
                  ...startEvent.ref,
                  DEPREACTED_DO_NOT_USE_backendId:
                    config.DEPREACTED_DO_NOT_USE_backendId
                      ? config.DEPREACTED_DO_NOT_USE_backendId(
                          event.body.result as any,
                        )
                      : undefined,
                },
              };
            }
            return {
              ...startEvent,
              state: 'error' as const,
              bytesTransferred: startEvent.totalBytes,
              error: {
                errorCode: event.body.errorCode,
                requestId: event.body.requestId,
              },
            } as UploadTaskEvent;
          }
        }),
        filter(isDefined),
        startWith(startEvent),
      );

    return new UploadTask(upload$);
  }
}

/**
 * Error returned from the tus-js-client library.
 * Needed because the library returns richer error codes
 * but doesn't export the type for it.
 */
interface TusError extends Error {
  originalResponse: TusResponse;
}

// Checks whether the error is a rich error returned from the tus-js-client
// library
function isTusError(error: Error): error is TusError {
  return 'originalResponse' in error;
}

// Transforms an Error type into a standardised file service error
function parseTusError(
  tusError: Error,
): BackendErrorResponse<ErrorCodeFileService> {
  const unknownFileServiceError = {
    status: 'error' as const,
    errorCode: 'UNKNOWN_ERROR' as const,
  };
  if (isTusError(tusError)) {
    try {
      const err = JSON.parse(tusError.originalResponse.getBody());
      return {
        status: err.status,
        errorCode: err.error_code,
        requestId: err.request_id,
      };
    } catch (e: any) {
      return unknownFileServiceError;
    }
  }
  return unknownFileServiceError;
}

// Error codes returned from the File Service backend
export enum ErrorCodeFileService {
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_FOUND = 'NOT_FOUND',
  PRECONDITION_FAILED = 'PRECONDITION_FAILED',
  REQUEST_ENTITY_TOO_LARGE = 'REQUEST_ENTITY_TOO_LARGE',
  CONFLICT = 'CONFLICT',
}
